import React from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { canUseConnections, can, isTcAdmin, isPlatformLevel, isAgentOnly } from './utils/roleUtils';
import { TenantProvider } from './contexts/TenantContext';
import TenantThemeProvider from './contexts/TenantThemeProvider';
// ThemeProvider moved to index.js for centralized theme management
import MainLayout from './components/Layout/MainLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AiChat from './pages/ai/AiChat';
import AiUsagePage from './pages/ai/AiUsagePage';
import Organizations from './pages/Organizations';
import Tickets from './pages/Tickets';
import Activity from './pages/Activity';
import DocumentExpiry from './pages/DocumentExpiry';
import Payments from './pages/Payments';
import NgeniusReturn from './pages/NgeniusReturn';
import NgeniusCancelled from './pages/NgeniusCancelled';
import TypingCentersList from './pages/TypingCentersList';
import TypingCenterDetail from './pages/TypingCenterDetail';
import ApiTest from './pages/ApiTest';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import CreateInvoice from './pages/CreateInvoice';
import PickWorkOrderItems from './pages/invoices/PickWorkOrderItems';
import CreateInvoiceFromWorkOrders from './pages/CreateInvoiceFromWorkOrders';
import Quotations from './pages/quotations/Quotations';
import CreateQuotation from './pages/quotations/CreateQuotation';
import QuotationDetail from './pages/quotations/QuotationDetail';
import WorkOrders from './pages/work-orders/WorkOrders';
import CreateWorkOrder from './pages/work-orders/CreateWorkOrder';
import CreateOnBehalfWorkOrder from './pages/work-orders/CreateOnBehalfWorkOrder';
import WorkOrderDetail from './pages/work-orders/WorkOrderDetail';
import TransactionLedger from './pages/TransactionLedger';
import CreateTicket from './pages/CreateTicket';
import { Wallets, WalletDetail, Banking } from './pages/wallets';
import ReceivedCheques from './pages/wallets/ReceivedCheques';
import AssetSettings from './pages/settings/AssetSettings';
import { InvoiceReceiptSummary, WalletBankReport, CollectionReport, CustomerAdvancesDebtors, ReportsHub, ProfitLoss, BalanceSheet, TrialBalance, GeneralLedger, ClientLedger, ClientStatement } from './pages/reports';
import InternalOrgDetail from './pages/organizations/InternalOrgDetail';
import Team from './pages/Team';
import MyCommissions from './pages/accounting/MyCommissions';
import Roles from './pages/settings/Roles';
import TcUserPasswordReset from './pages/Admin/TcUserPasswordReset';
// Subscription imports
import { AdminSubscriptions } from './pages/subscription';
// Platform admin (billing infra) imports
import PlatformBanks from './pages/Admin/PlatformBanks';
import AiPlatformUsage from './pages/Admin/AiPlatformUsage';
import PaymentGateways from './pages/Admin/PaymentGateways';
import SubscriptionPlansCatalog from './pages/Admin/SubscriptionPlansCatalog';
// Accounting imports
import {
  CreateExpense,
  ExpenseDetail,
  Commissions,
  CommissionConfig,
  CommissionRecipients,
  CreditCardDetail,
  CreditCards,
  DisbursementReviews,
  PartnerPayments,
  OwnerDrawings,
  Partners,
  Reconciliation,
  ReconciliationSession,
  JournalEntries,
  CreateJournalEntry,
  JournalEntryDetail,
  FixedAssets,
  FinancialHealthCheck
} from './pages/accounting';
// Receipt imports
import { Receipts, ReceiptDetail } from './pages/receipts';
// Settlement imports
import { Settlements, SettlementDetail, CreateSettlement } from './pages/settlements';
// Service Catalog imports
import { ServiceCatalog, ServiceMappings } from './pages/service-catalog';
// Task imports
import { Tasks } from './pages/tasks';
// Admin imports
import TenantSettings from './pages/Admin/TenantSettings';
import SettingsHub from './pages/settings/SettingsHub';
import SubscriptionTabs from './pages/subscription/SubscriptionTabs';
import TaxTabs from './pages/reports/TaxTabs';
import CashFlow from './pages/reports/CashFlow';
import CashPosition from './pages/reports/CashPosition';
import ReceivablesAging from './pages/reports/ReceivablesAging';
import InvoiceReport from './pages/reports/InvoiceReport';
import ServiceProfit from './pages/reports/ServiceProfit';
import AgentPerformance from './pages/reports/AgentPerformance';
import StaffCommission from './pages/reports/StaffCommission';
import SalaryReport from './pages/reports/SalaryReport';
import SupplierReport from './pages/reports/SupplierReport';
import DailySheet from './pages/reports/DailySheet';
import InactiveClients from './pages/reports/InactiveClients';
import AnnualPl from './pages/reports/AnnualPl';
import ExpenseReport from './pages/reports/ExpenseReport';
import ExpenseTabs from './pages/accounting/ExpenseTabs';
// Portal imports
import PortalLogin from './pages/portal/PortalLogin';
import PortalAutoLogin from './pages/portal/PortalAutoLogin';
import PortalCustomers from './pages/portal/PortalCustomers';
import ConnectionsIndex from './pages/settings/connections/ConnectionsIndex';
import ProvidersPage from './pages/settings/connections/ProvidersPage';
import ClientsPage from './pages/settings/connections/ClientsPage';
import ConnectionInquiriesPage from './pages/settings/connections/InquiriesPage';
import DiscoveryPage from './pages/settings/connections/DiscoveryPage';
import GroupLogin from './pages/group/GroupLogin';
import GroupLayout from './pages/group/GroupLayout';
import GroupDashboard from './pages/group/GroupDashboard';
import GroupCenters from './pages/group/GroupCenters';
import TypingCenterGroups from './pages/Admin/TypingCenterGroups';
import PortalLayout from './pages/portal/PortalLayout';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalQuotations from './pages/portal/PortalQuotations';
import PortalQuotationDetail from './pages/portal/PortalQuotationDetail';
import PortalWorkOrders from './pages/portal/PortalWorkOrders';
import PortalWorkOrderDetail from './pages/portal/PortalWorkOrderDetail';
import PortalCreateWorkOrder from './pages/portal/PortalCreateWorkOrder';
import PortalTasks from './pages/portal/PortalTasks';
import PortalEmployees from './pages/portal/PortalEmployees';
import PortalInvoices from './pages/portal/PortalInvoices';
import PortalDocumentExpiry from './pages/portal/PortalDocumentExpiry';
import PortalCompanyDocuments from './pages/portal/PortalCompanyDocuments';
import PortalInvoiceDetail from './pages/portal/PortalInvoiceDetail';
import PortalWallet from './pages/portal/PortalWallet';
import PortalTopUp from './pages/portal/PortalTopUp';
import PortalPayInvoice from './pages/portal/PortalPayInvoice';
import PortalPayments from './pages/portal/PortalPayments';
import PortalStatement from './pages/portal/PortalStatement';
import PortalServiceSettings from './pages/portal/PortalServiceSettings';
import PortalCards from './pages/portal/PortalCards';
import './App.css';

function App() {
  // Mock authentication state (will be replaced with real auth in Phase 2)
  // Use localStorage to persist auth state across hot reloads
  const [isAuthenticated, setIsAuthenticated] = React.useState(() => {
    const stored = localStorage.getItem('isAuthenticated') === 'true';
    console.log('🔐 [App Init] isAuthenticated from localStorage:', stored);
    return stored;
  });
  const [currentUser, setCurrentUser] = React.useState(() => {
    const stored = localStorage.getItem('currentUser');
    const user = stored ? JSON.parse(stored) : null;
    console.log('👤 [App Init] currentUser from localStorage:', user);
    return user;
  });

  const handleLogin = (userData) => {
    console.log('✅ [App] Login successful, userData:', userData);
    setCurrentUser(userData);
    setIsAuthenticated(true);
    // Persist to localStorage
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('currentUser', JSON.stringify(userData));
    console.log('💾 [App] Saved to localStorage');
  };

  // Portal authentication state
  const [isPortalAuthenticated, setIsPortalAuthenticated] = React.useState(() => {
    return localStorage.getItem('portalAuthenticated') === 'true';
  });
  const [portalUser, setPortalUser] = React.useState(() => {
    const stored = localStorage.getItem('portalUser');
    return stored ? JSON.parse(stored) : null;
  });

  const handlePortalLogin = (userData) => {
    console.log('✅ [App] Portal login successful, userData:', userData);
    setPortalUser(userData);
    setIsPortalAuthenticated(true);
    localStorage.setItem('portalAuthenticated', 'true');
    localStorage.setItem('portalUser', JSON.stringify(userData));
  };

  const handlePortalLogout = () => {
    setPortalUser(null);
    setIsPortalAuthenticated(false);
    setIsPortalGroupAuthenticated(false);
    localStorage.removeItem('portalAuthenticated');
    localStorage.removeItem('portalUser');
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalOrganization');
    localStorage.removeItem('actingAsPortalGroupUser');
    localStorage.removeItem('portalGroupAuthenticated');
    localStorage.removeItem('portalGroupToken');
    localStorage.removeItem('portalGroupUser');
    localStorage.removeItem('portalGroupData');
  };

  const [isPortalGroupAuthenticated, setIsPortalGroupAuthenticated] = React.useState(() => {
    return localStorage.getItem('portalGroupAuthenticated') === 'true';
  });

  const handlePortalGroupLogin = () => {
    setIsPortalGroupAuthenticated(true);
    localStorage.setItem('portalGroupAuthenticated', 'true');
  };

  const handlePortalGroupLogout = () => {
    setPortalUser(null);
    setIsPortalAuthenticated(false);
    setIsPortalGroupAuthenticated(false);
    localStorage.removeItem('portalAuthenticated');
    localStorage.removeItem('portalUser');
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalOrganization');
    localStorage.removeItem('actingAsPortalGroupUser');
    localStorage.removeItem('portalGroupAuthenticated');
    localStorage.removeItem('portalGroupToken');
    localStorage.removeItem('portalGroupUser');
    localStorage.removeItem('portalGroupData');
  };

  // Group-owner (multi-TC) authentication state
  const [isGroupAuthenticated, setIsGroupAuthenticated] = React.useState(() => {
    return localStorage.getItem('groupAuthenticated') === 'true';
  });

  const handleGroupLogin = () => {
    setIsGroupAuthenticated(true);
    localStorage.setItem('groupAuthenticated', 'true');
  };

  const handleGroupLogout = () => {
    setIsGroupAuthenticated(false);
    localStorage.removeItem('groupAuthenticated');
    localStorage.removeItem('groupToken');
    localStorage.removeItem('groupUser');
    localStorage.removeItem('groupData');
  };

  // Log whenever auth state changes
  React.useEffect(() => {
    console.log('🔄 [App] Auth state changed - isAuthenticated:', isAuthenticated, 'currentUser:', currentUser);
  }, [isAuthenticated, currentUser]);

  return (
    <Sentry.ErrorBoundary fallback={<div style={{padding: '40px', textAlign: 'center'}}><h2>Something went wrong</h2><p>The error has been reported. Please refresh the page.</p><button onClick={() => window.location.reload()}>Refresh</button></div>}>
    <TenantProvider>
      <TenantThemeProvider>
        <Router>
          <Routes>
        <Route
          path="/login"
          element={<Login onLogin={handleLogin} />}
        />
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? (
              isAgentOnly(currentUser)
                ? <Navigate to="/my-commissions" />
                : <MainLayout currentUser={currentUser}><Dashboard /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/ai-assistant"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><AiChat currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/ai-assistant/usage"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><AiUsagePage currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route path="/settings/ai" element={<Navigate to="/settings?section=ai&tab=settings" replace />} />
        <Route path="/settings/ai/usage" element={<Navigate to="/ai-assistant/usage" replace />} />
        <Route
          path="/organizations"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><Organizations currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/tickets"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><Tickets /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route path="/my-commissions" element={isAuthenticated ? <MainLayout currentUser={currentUser}><MyCommissions /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/settings/roles" element={
          isAuthenticated
            ? ((isTcAdmin(currentUser?.role) || isPlatformLevel(currentUser?.role))
                ? <MainLayout currentUser={currentUser}><Roles currentUser={currentUser} /></MainLayout>
                : <Navigate to="/dashboard" />)
            : <Navigate to="/login" />
        } />
        {/* Team (members + roles) is admin-only: user/role management is not a matrix feature. */}
        <Route path="/team" element={
          isAuthenticated
            ? ((isTcAdmin(currentUser?.role) || isPlatformLevel(currentUser?.role))
                ? <MainLayout currentUser={currentUser}><Team currentUser={currentUser} /></MainLayout>
                : <Navigate to="/dashboard" />)
            : <Navigate to="/login" />
        } />
        <Route
          path="/activity"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><Activity /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/document-expiry"
          element={
            isAuthenticated ? (
              can(currentUser, 'thaklis_features', 'read') ? (
                <MainLayout currentUser={currentUser}><DocumentExpiry currentUser={currentUser} /></MainLayout>
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/payments"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><Payments /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/payments/ngenius/return"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><NgeniusReturn /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/payments/ngenius/cancelled"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><NgeniusCancelled /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/payments/cheques"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><ReceivedCheques /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/reports/invoice-receipt-summary"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><InvoiceReceiptSummary /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/reports/wallet-bank"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><WalletBankReport /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/reports/collection"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><CollectionReport /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/reports/customer-advances-debtors"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><CustomerAdvancesDebtors /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/typing-centers"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><TypingCentersList /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/typing-centers/:centerId"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><TypingCenterDetail /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/api-test"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><ApiTest /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/invoices"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><Invoices /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/invoices/create"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><CreateInvoice /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/invoices/new/items"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><PickWorkOrderItems currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/invoices/create-from-work-orders"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><CreateInvoiceFromWorkOrders currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/invoices/:invoiceId"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><InvoiceDetail /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route path="/invoice-config" element={<Navigate to="/settings?section=documents&tab=invoices" replace />} />
        <Route path="/work-order-config" element={<Navigate to="/settings?section=documents&tab=work-orders" replace />} />
        <Route path="/email-config" element={<Navigate to="/settings?section=communication&tab=email" replace />} />
        <Route path="/ai-config" element={<Navigate to="/settings?section=ai&tab=settings" replace />} />
        <Route path="/notification-settings" element={<Navigate to="/settings?section=communication&tab=notifications" replace />} />
        <Route path="/company-config" element={<Navigate to="/settings?section=company&tab=details" replace />} />
        <Route
          path="/quotations"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><Quotations /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route path="/tax" element={<Navigate to="/reports/vat-return" replace />} />
        <Route
          path="/quotations/create"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><CreateQuotation currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/quotations/:quotationId"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><QuotationDetail /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/quotations/:quotationId/edit"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><CreateQuotation currentUser={currentUser} isEditMode={true} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/work-orders"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><WorkOrders currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/work-orders/create"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><CreateWorkOrder currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/work-orders/create-on-behalf"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><CreateOnBehalfWorkOrder currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/work-orders/:workOrderId"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><WorkOrderDetail currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/work-orders/:workOrderId/edit"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><CreateWorkOrder currentUser={currentUser} isEditMode={true} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* ==================== Receipts Routes ==================== */}
        <Route path="/receipts" element={isAuthenticated ? <MainLayout currentUser={currentUser}><Receipts currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/receipts/:id" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ReceiptDetail /></MainLayout> : <Navigate to="/login" />} />
        {/* ==================== Walk-in Routes ==================== */}
        {/* ==================== Credit Routes ==================== */}
        {/* ==================== Settlement Routes ==================== */}
        <Route path="/settlements" element={isAuthenticated ? <MainLayout currentUser={currentUser}><Settlements currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/settlements/create" element={isAuthenticated ? <MainLayout currentUser={currentUser}><CreateSettlement currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/settlements/:id" element={isAuthenticated ? <MainLayout currentUser={currentUser}><SettlementDetail /></MainLayout> : <Navigate to="/login" />} />
        {/* ==================== Service Catalog Routes ==================== */}
        <Route path="/service-catalog" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ServiceCatalog currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/service-catalog/mappings" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ServiceMappings currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        {/* ==================== Tasks Routes ==================== */}
        <Route path="/tasks" element={isAuthenticated ? <MainLayout currentUser={currentUser}><Tasks currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route
          path="/tickets/create"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><CreateTicket /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/transactions"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><TransactionLedger currentUser={currentUser} /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* Wallet Routes */}
        <Route
          path="/wallets"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><Wallets /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/wallets/:id"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><WalletDetail /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/banking"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><Banking /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* Organization Ledger Routes */}
        <Route
          path="/organizations/internal/:orgId"
          element={
            isAuthenticated ? (
              <MainLayout currentUser={currentUser}><InternalOrgDetail /></MainLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* My Account now lives in Settings › Your account; old links redirect. */}
        <Route path="/account" element={<Navigate to="/settings?section=account" replace />} />
        <Route path="/settings" element={isAuthenticated ? <MainLayout currentUser={currentUser}><SettingsHub currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/account/profile" element={<Navigate to="/settings?section=account" replace />} />
        <Route path="/account/change-password" element={<Navigate to="/settings?section=account" replace />} />
        <Route
          path="/admin/reset-tc-password"
          element={
            isAuthenticated ? (
              currentUser?.role === 'PLATFORM_ADMIN' ? (
                <MainLayout currentUser={currentUser}><TcUserPasswordReset /></MainLayout>
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/typing-center-groups"
          element={
            isAuthenticated ? (
              currentUser?.role === 'PLATFORM_ADMIN' ? (
                <MainLayout currentUser={currentUser}><TypingCenterGroups /></MainLayout>
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* ==================== Subscription Routes ==================== */}
        <Route path="/settings/subscription" element={isAuthenticated ? <MainLayout currentUser={currentUser}><SubscriptionTabs currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/settings/subscription/usage" element={<Navigate to="/settings/subscription?tab=usage" replace />} />
        <Route path="/settings/subscription/billing" element={<Navigate to="/settings/subscription?tab=billing" replace />} />
        <Route
          path="/admin/subscriptions"
          element={
            isAuthenticated ? (
              currentUser?.role === 'PLATFORM_ADMIN' ? (
                <MainLayout currentUser={currentUser}><AdminSubscriptions currentUser={currentUser} /></MainLayout>
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/banks"
          element={
            isAuthenticated ? (
              currentUser?.role === 'PLATFORM_ADMIN' ? (
                <MainLayout currentUser={currentUser}><PlatformBanks currentUser={currentUser} /></MainLayout>
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/ai-usage"
          element={
            isAuthenticated ? (
              currentUser?.role === 'PLATFORM_ADMIN' ? (
                <MainLayout currentUser={currentUser}><AiPlatformUsage currentUser={currentUser} /></MainLayout>
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/payment-gateways"
          element={
            isAuthenticated ? (
              currentUser?.role === 'PLATFORM_ADMIN' ? (
                <MainLayout currentUser={currentUser}><PaymentGateways currentUser={currentUser} /></MainLayout>
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/subscription-plans"
          element={
            isAuthenticated ? (
              currentUser?.role === 'PLATFORM_ADMIN' ? (
                <MainLayout currentUser={currentUser}><SubscriptionPlansCatalog currentUser={currentUser} /></MainLayout>
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* ==================== Accounting Routes ==================== */}
        {/* ==================== TC-Level Accounting Routes ==================== */}
        <Route path="/accounting/expenses" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ExpenseTabs currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/expenses/new" element={isAuthenticated ? <MainLayout currentUser={currentUser}><CreateExpense currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/expenses/:id" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ExpenseDetail currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/expenses/:id/edit" element={isAuthenticated ? <MainLayout currentUser={currentUser}><CreateExpense currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/commissions" element={isAuthenticated ? <MainLayout currentUser={currentUser}><Commissions currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/commissions/config" element={isAuthenticated ? <MainLayout currentUser={currentUser}><CommissionConfig currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/commissions/recipients" element={isAuthenticated ? <MainLayout currentUser={currentUser}><CommissionRecipients currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/disbursement-reviews" element={isAuthenticated ? <MainLayout currentUser={currentUser}><DisbursementReviews currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/partner-payments" element={isAuthenticated ? <MainLayout currentUser={currentUser}><PartnerPayments currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/credit-cards" element={isAuthenticated ? <MainLayout currentUser={currentUser}><CreditCards currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/credit-cards/:id" element={isAuthenticated ? <MainLayout currentUser={currentUser}><CreditCardDetail currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/drawings" element={isAuthenticated ? <MainLayout currentUser={currentUser}><OwnerDrawings currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/partners" element={isAuthenticated ? <MainLayout currentUser={currentUser}><Partners currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/reconciliation" element={isAuthenticated ? <MainLayout currentUser={currentUser}><Reconciliation currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/reconciliation/new" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ReconciliationSession currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/reconciliation/:id" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ReconciliationSession currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/journal-entries" element={isAuthenticated ? <MainLayout currentUser={currentUser}><JournalEntries currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/journal-entries/new" element={isAuthenticated ? <MainLayout currentUser={currentUser}><CreateJournalEntry currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/journal-entries/:id" element={isAuthenticated ? <MainLayout currentUser={currentUser}><JournalEntryDetail currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/health" element={isAuthenticated ? <MainLayout currentUser={currentUser}><FinancialHealthCheck currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/accounting/assets" element={isAuthenticated ? <MainLayout currentUser={currentUser}><FixedAssets currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        {/* ==================== TC-Level Reports Routes ==================== */}
        <Route path="/reports" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ReportsHub currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/profit-loss" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ProfitLoss currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/balance-sheet" element={isAuthenticated ? <MainLayout currentUser={currentUser}><BalanceSheet currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/trial-balance" element={isAuthenticated ? <MainLayout currentUser={currentUser}><TrialBalance currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/general-ledger" element={isAuthenticated ? <MainLayout currentUser={currentUser}><GeneralLedger currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/vat-return" element={isAuthenticated ? <MainLayout currentUser={currentUser}><TaxTabs currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/cash-flow" element={isAuthenticated ? <MainLayout currentUser={currentUser}><CashFlow /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/cash-position" element={isAuthenticated ? <MainLayout currentUser={currentUser}><CashPosition /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/receivables-aging" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ReceivablesAging /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/invoice-report" element={isAuthenticated ? <MainLayout currentUser={currentUser}><InvoiceReport /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/service-profit" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ServiceProfit /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/agent-performance" element={isAuthenticated ? <MainLayout currentUser={currentUser}><AgentPerformance /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/salary" element={isAuthenticated ? <MainLayout currentUser={currentUser}><SalaryReport currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/staff-commission" element={isAuthenticated ? <MainLayout currentUser={currentUser}><StaffCommission /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/suppliers" element={isAuthenticated ? <MainLayout currentUser={currentUser}><SupplierReport /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/daily-sheet" element={isAuthenticated ? <MainLayout currentUser={currentUser}><DailySheet /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/inactive-clients" element={isAuthenticated ? <MainLayout currentUser={currentUser}><InactiveClients /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/annual-pl" element={isAuthenticated ? <MainLayout currentUser={currentUser}><AnnualPl /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/expenses" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ExpenseReport /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/client-ledger" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ClientLedger currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        <Route path="/reports/client-ledger/:orgId" element={isAuthenticated ? <MainLayout currentUser={currentUser}><ClientStatement currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        {/* ==================== TC-Level Settings Routes ==================== */}
        <Route path="/settings/vat" element={<Navigate to="/reports/vat-return?tab=settings" replace />} />
        <Route path="/settings/expense-categories" element={<Navigate to="/accounting/expenses?tab=categories" replace />} />
        <Route path="/settings/service-providers" element={<Navigate to="/service-catalog?tab=providers" replace />} />
        <Route path="/settings/asset-config" element={isAuthenticated ? <MainLayout currentUser={currentUser}><AssetSettings currentUser={currentUser} /></MainLayout> : <Navigate to="/login" />} />
        {/* ==================== Tenant Settings Route ==================== */}
        <Route
          path="/settings/tenant"
          element={
            isAuthenticated ? (
              isPlatformLevel(currentUser?.role) ? (
                <MainLayout currentUser={currentUser}><TenantSettings currentUser={currentUser} /></MainLayout>
              ) : (
                <Navigate to="/settings?section=company&tab=branding" replace />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* ==================== Connections Routes (promoted TCs + Providers) ==================== */}
        <Route path="/settings/connections" element={
          isAuthenticated
            ? ((can(currentUser, 'connections', 'read') && (canUseConnections(currentUser) || currentUser?.organizationType === 'B2B'))
                ? <MainLayout currentUser={currentUser}><ConnectionsIndex currentUser={currentUser} /></MainLayout>
                : <Navigate to="/dashboard" />)
            : <Navigate to="/login" />
        } />
        <Route path="/settings/connections/providers" element={
          isAuthenticated
            ? (can(currentUser, 'connections', 'read') && canUseConnections(currentUser) && currentUser?.organizationType !== 'B2B'
                ? <MainLayout currentUser={currentUser}><ProvidersPage currentUser={currentUser} /></MainLayout>
                : (can(currentUser, 'connections', 'read') && currentUser?.organizationType === 'B2B'
                    ? <Navigate to="/settings/connections/clients" />
                    : <Navigate to="/dashboard" />))
            : <Navigate to="/login" />
        } />
        <Route path="/settings/connections/clients" element={
          isAuthenticated
            ? (can(currentUser, 'connections', 'read') && currentUser?.organizationType === 'B2B'
                ? <MainLayout currentUser={currentUser}><ClientsPage currentUser={currentUser} /></MainLayout>
                : (can(currentUser, 'connections', 'read') && canUseConnections(currentUser)
                    ? <Navigate to="/settings/connections/providers" />
                    : <Navigate to="/dashboard" />))
            : <Navigate to="/login" />
        } />
        <Route path="/settings/connections/inquiries" element={
          isAuthenticated
            ? ((can(currentUser, 'connections', 'read') && (canUseConnections(currentUser) || currentUser?.organizationType === 'B2B'))
                ? <MainLayout currentUser={currentUser}><ConnectionInquiriesPage currentUser={currentUser} /></MainLayout>
                : <Navigate to="/dashboard" />)
            : <Navigate to="/login" />
        } />
        <Route path="/settings/connections/discovery" element={
          isAuthenticated
            ? (can(currentUser, 'connections', 'read') && currentUser?.organizationType === 'B2B'
                ? <MainLayout currentUser={currentUser}><DiscoveryPage currentUser={currentUser} /></MainLayout>
                : <Navigate to="/dashboard" />)
            : <Navigate to="/login" />
        } />
        {/* ==================== Group Owner Routes ==================== */}
        <Route
          path="/group/login"
          element={<GroupLogin onGroupLogin={handleGroupLogin} />}
        />
        <Route
          path="/group/centers"
          element={
            isGroupAuthenticated ? (
              <GroupLayout onGroupLogout={handleGroupLogout}>
                <GroupCenters />
              </GroupLayout>
            ) : (
              <Navigate to="/group/login" />
            )
          }
        />
        <Route
          path="/group/dashboard"
          element={
            isGroupAuthenticated ? (
              <GroupLayout onGroupLogout={handleGroupLogout}>
                <GroupDashboard />
              </GroupLayout>
            ) : (
              <Navigate to="/group/login" />
            )
          }
        />
        {/* ==================== Portal Routes ==================== */}
        <Route
          path="/portal/login"
          element={<PortalLogin onPortalLogin={handlePortalLogin} onPortalGroupLogin={handlePortalGroupLogin} />}
        />
        <Route
          path="/portal/customers"
          element={
            isPortalGroupAuthenticated ? (
              <PortalCustomers onPortalGroupLogout={handlePortalGroupLogout} />
            ) : (
              <Navigate to={isPortalAuthenticated ? '/portal/dashboard' : '/portal/login'} />
            )
          }
        />
        <Route
          path="/portal/auto-login"
          element={<PortalAutoLogin onPortalLogin={handlePortalLogin} />}
        />
        <Route
          path="/portal/dashboard"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalDashboard portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/quotations"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalQuotations portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/quotations/:quotationId"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalQuotationDetail portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/work-orders"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalWorkOrders portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/work-orders/create"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalCreateWorkOrder portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/work-orders/:workOrderId"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalWorkOrderDetail portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/employees"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalEmployees portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/company-documents"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalCompanyDocuments portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/tasks"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalTasks portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/invoices"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalInvoices portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/document-expiry"
          element={
            isPortalAuthenticated ? (
              portalUser?.docExpiryEnabled !== false ? (
                <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                  <PortalDocumentExpiry portalUser={portalUser} />
                </PortalLayout>
              ) : (
                <Navigate to="/portal/dashboard" />
              )
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/service-settings"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalServiceSettings portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/cards"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalCards portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/invoices/:invoiceId"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalInvoiceDetail portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/invoices/:invoiceId/pay"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalPayInvoice portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/payments"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalPayments portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/statements"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalStatement />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/wallet"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalWallet portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
        <Route
          path="/portal/wallet/topup"
          element={
            isPortalAuthenticated ? (
              <PortalLayout portalUser={portalUser} onPortalLogout={handlePortalLogout}>
                <PortalTopUp portalUser={portalUser} />
              </PortalLayout>
            ) : (
              <Navigate to="/portal/login" />
            )
          }
        />
          <Route
            path="/"
            element={<Navigate to={isAuthenticated ? (isAgentOnly(currentUser) ? '/my-commissions' : '/dashboard') : '/login'} />}
          />
        {/* Removed legacy pages (Wave 5b): send old links to their real replacements */}
        <Route path="/walk-in" element={<Navigate to="/work-orders/create" replace />} />
        <Route path="/credits/*" element={<Navigate to="/wallets" replace />} />
        <Route path="/organizations/:id/ledger" element={<Navigate to="/organizations" replace />} />
        <Route path="/organizations/:id/statement" element={<Navigate to="/organizations" replace />} />
        <Route path="/admin/accounting/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/settings/payment-gateway" element={<Navigate to="/admin/payment-gateways" replace />} />
        </Routes>
        </Router>
      </TenantThemeProvider>
    </TenantProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
